// ============================================================================
// Supply → Chemicals.
//
// 2026-09-09, facility-chemicals-in-app. Two jobs on one page:
//
//   1. The review list — every typed-in product name with no catalogue match,
//      grouped by name, with the buildings using it. This is what keeps the
//      catalogue current: Ben reads it and decides whether a product deserves
//      an entry. ⛔ Nothing here guesses a match.
//   2. Which clinic uses which chemical (added in the same task, Phase 3).
//
// No charts and no trends. A list that answers the question.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, ErrorState, PageHeader, Skeleton } from '../../shared/components';
import { registerReader } from '../../shared/save-path';
import { errorMessage } from '../../shared/errors';
import {
  fetchChemicalPortfolio,
  fetchUnmatchedChemicals,
  groupByBuilding,
  groupByChemical,
  managedByLabel,
  type ChemicalManagedBy,
  type ChemicalPortfolio,
  type UnmatchedChemicalGroup,
} from '../../shared/buildings/facility-chemicals';
import { SupplySubNav } from './supply-sub-nav';

const CONTROL =
  'rounded-lg border border-tcs-border bg-tcs-surface px-2 py-1.5 text-sm text-tcs-text';

export default function ChemicalsPage() {
  const [groups, setGroups] = useState<UnmatchedChemicalGroup[]>([]);
  const [portfolio, setPortfolio] = useState<ChemicalPortfolio | null>(null);
  const [market, setMarket] = useState<string>('');
  const [supplier, setSupplier] = useState<ChemicalManagedBy | 'unknown' | ''>('');
  const [view, setView] = useState<'chemical' | 'building'>('chemical');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  useEffect(
    () => registerReader(['facility_chemicals', 'supplies'], refetch),
    [refetch],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      try {
        const [rows, port] = await Promise.all([
          fetchUnmatchedChemicals(),
          fetchChemicalPortfolio(),
        ]);
        if (cancelled) return;
        setGroups(rows);
        setPortfolio(port);
      } catch (e) {
        if (cancelled) return;
        console.error('[chemicals-page] load failed', e);
        setError(errorMessage(e, 'Could not load what each building is using.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const uses = groups.reduce((n, g) => n + g.buildings.length, 0);

  const filtered = (portfolio?.rows ?? []).filter((r) => {
    if (market && r.market_region !== market) return false;
    if (supplier === 'unknown' && r.managed_by !== null) return false;
    if ((supplier === 'TCS' || supplier === 'Facility') && r.managed_by !== supplier) {
      return false;
    }
    return true;
  });
  const byChemical = groupByChemical(filtered);
  const byBuilding = groupByBuilding(filtered);
  const throughPointer = filtered.filter((r) => r.resolved_through_pointer).length;

  return (
    <div>
      <SupplySubNav />
      <PageHeader
        title="Chemicals"
        meta="What each building is using, and the typed-in names waiting on a catalogue decision"
      />

      {loading && <Skeleton width="100%" height={120} block />}

      {!loading && error && (
        <ErrorState title="Couldn't load the chemicals" body={error} onRetry={refetch} />
      )}

      {!loading && !error && portfolio && (
        <section className="mt-4 rounded-xl border border-tcs-border bg-tcs-bg p-4 sm:p-5">
          <h2 className="text-sm font-semibold text-tcs-text">
            Which building uses which chemical
          </h2>
          {/* The coverage number, said out loud. Hiding it makes the buildings
              with nothing recorded invisible, which is how five months of
              nothing went unnoticed. */}
          <p className="mt-1 text-xs text-tcs-text-muted">
            {portfolio.activeFacilitiesWithSomething} of {portfolio.activeFacilities}{' '}
            active buildings have a chemical recorded.{' '}
            <span className="font-medium text-tcs-text-secondary">
              {portfolio.activeFacilitiesWithNothing} have none.
            </span>
            {throughPointer > 0
              ? ` ${throughPointer} ${throughPointer === 1 ? 'row was' : 'rows were'} recorded against a duplicate catalogue item and ${throughPointer === 1 ? 'counts' : 'count'} toward the item that survives.`
              : ''}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select
              className={CONTROL}
              value={view}
              onChange={(e) => setView(e.target.value as 'chemical' | 'building')}
              aria-label="Group by"
            >
              <option value="chemical">By chemical</option>
              <option value="building">By building</option>
            </select>
            <select
              className={CONTROL}
              value={market}
              onChange={(e) => setMarket(e.target.value)}
              aria-label="Market"
            >
              <option value="">Every market</option>
              {portfolio.markets.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <select
              className={CONTROL}
              value={supplier}
              onChange={(e) =>
                setSupplier(e.target.value as ChemicalManagedBy | 'unknown' | '')
              }
              aria-label="Who supplies it"
            >
              <option value="">Supplied by anyone</option>
              <option value="TCS">We supply it</option>
              <option value="Facility">The facility supplies it</option>
              <option value="unknown">Not known</option>
            </select>
          </div>

          {filtered.length === 0 && (
            <div className="mt-3">
              <EmptyState
                title="Nothing recorded for that filter."
                body="Widen the market or the supplier."
              />
            </div>
          )}

          {filtered.length > 0 && view === 'chemical' && (
            <ul className="mt-3 divide-y divide-tcs-border">
              {byChemical.map((g) => (
                <li key={g.key} className="py-2.5">
                  <p className="text-sm text-tcs-text">
                    {g.name}
                    {g.unmatched && (
                      <span className="ml-2 rounded-full border border-tcs-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-tcs-text-muted">
                        not on the list
                      </span>
                    )}
                    <span className="ml-2 text-xs font-normal text-tcs-text-muted">
                      {g.buildings} {g.buildings === 1 ? 'building' : 'buildings'}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-tcs-text-muted">
                    {g.rows
                      .map((r) => r.facility_name ?? r.facility_id)
                      .sort((a, b) => a.localeCompare(b))
                      .join(' · ')}
                  </p>
                </li>
              ))}
            </ul>
          )}

          {filtered.length > 0 && view === 'building' && (
            <ul className="mt-3 divide-y divide-tcs-border">
              {byBuilding.map((b) => (
                <li key={b.facility_id} className="py-2.5">
                  <p className="text-sm text-tcs-text">
                    <Link
                      to={`/account-management/facilities/${b.facility_id}?tab=supplies`}
                      className="underline"
                    >
                      {b.facility_name}
                    </Link>
                    <span className="ml-2 text-xs font-normal text-tcs-text-muted">
                      {b.market_region ?? 'market not set'} · {b.rows.length}{' '}
                      {b.rows.length === 1 ? 'chemical' : 'chemicals'}
                    </span>
                  </p>
                  <ul className="mt-0.5 space-y-0.5">
                    {b.rows.map((r) => (
                      <li key={r.fc_id} className="text-xs text-tcs-text-muted">
                        {r.effective_name}
                        {r.unmatched ? ' (not on the list)' : ''} ·{' '}
                        {managedByLabel(r.managed_by)}
                        {r.resolved_through_pointer
                          ? ` · recorded as ${r.recorded_supply_id}`
                          : ''}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="mt-4 rounded-xl border border-tcs-border bg-tcs-bg p-4 sm:p-5">
        <h2 className="text-sm font-semibold text-tcs-text">
          Typed in, not on the list
          <span className="ml-2 text-xs font-normal text-tcs-text-muted">
            ({groups.length} {groups.length === 1 ? 'name' : 'names'}
            {uses > 0 ? `, ${uses} ${uses === 1 ? 'building' : 'buildings'}` : ''})
          </span>
        </h2>
        <p className="mt-1 text-xs text-tcs-text-muted">
          Somebody recorded these by typing the product name because it was not
          in the catalogue. Each one is either a product worth adding, or a
          customer-supplied product we do not buy and should not add.
        </p>

        {!loading && !error && groups.length === 0 && (
          <div className="mt-3">
            <EmptyState
              title="Nothing waiting."
              body="Every chemical recorded anywhere matches a catalogue item."
            />
          </div>
        )}

        {!loading && !error && groups.length > 0 && (
          <ul className="mt-3 divide-y divide-tcs-border">
            {groups.map((g) => (
              <li key={g.name.toLowerCase()} className="py-3">
                <p className="text-sm font-medium text-tcs-text">
                  {g.name}
                  <span className="ml-2 text-xs font-normal text-tcs-text-muted">
                    {g.buildings.length}{' '}
                    {g.buildings.length === 1 ? 'building' : 'buildings'}
                  </span>
                </p>
                <ul className="mt-1 space-y-1">
                  {g.buildings.map((b) => (
                    <li key={b.fc_id} className="text-xs text-tcs-text-muted">
                      <Link
                        to={`/account-management/facilities/${b.facility_id}?tab=supplies`}
                        className="text-tcs-text-secondary underline"
                      >
                        {b.facility_name ?? b.facility_id}
                      </Link>
                      {' · '}
                      {managedByLabel(b.managed_by)}
                      {b.notes ? ` · ${b.notes}` : ''}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
